import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { CheckIcon, ChevronDownIcon } from "lucide-react";

import { cn } from "@/src/js/lib/utils";

// The look of the dashboard's dropdowns, shared with other components that should match them
export const DROPDOWN_TRIGGER_CLASS =
  "tw:border-neutral-200 tw:data-[placeholder]:text-neutral-500 tw:[&_svg:not([class*=text-])]:text-neutral-500 tw:focus-visible:border-neutral-950 tw:focus-visible:ring-neutral-950/50 tw:flex tw:w-fit tw:items-center tw:justify-between tw:gap-2 tw:rounded-md tw:border tw:bg-transparent tw:px-3 tw:py-2 tw:text-sm tw:whitespace-nowrap tw:shadow-xs tw:transition-[color,box-shadow] tw:outline-none tw:focus-visible:ring-[3px] tw:disabled:cursor-not-allowed tw:disabled:opacity-50 tw:h-9 tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0 tw:[&_svg:not([class*=size-])]:size-4 tw:dark:border-neutral-800 tw:dark:data-[placeholder]:text-neutral-400 tw:dark:[&_svg:not([class*=text-])]:text-neutral-400 tw:dark:focus-visible:border-neutral-300 tw:dark:focus-visible:ring-neutral-300/50";
export const DROPDOWN_CONTENT_CLASS =
  "tw:bg-white tw:text-neutral-950 tw:data-[state=open]:animate-in tw:data-[state=open]:fade-in-0 tw:data-[state=open]:zoom-in-95 tw:data-[side=bottom]:slide-in-from-top-2 tw:data-[side=left]:slide-in-from-right-2 tw:data-[side=right]:slide-in-from-left-2 tw:data-[side=top]:slide-in-from-bottom-2 tw:relative tw:z-50 tw:max-h-[300px] tw:min-w-[8rem] tw:overflow-y-auto tw:rounded-md tw:border tw:border-neutral-200 tw:p-1 tw:shadow-md tw:dark:bg-neutral-950 tw:dark:text-neutral-50 tw:dark:border-neutral-800";
export const DROPDOWN_ITEM_CLASS =
  "tw:focus:bg-neutral-100 tw:focus:text-neutral-900 tw:relative tw:flex tw:w-full tw:cursor-default tw:items-center tw:gap-2 tw:rounded-sm tw:py-1.5 tw:pr-8 tw:pl-2 tw:text-sm tw:outline-hidden tw:select-none tw:data-[disabled]:pointer-events-none tw:data-[disabled]:opacity-50 tw:dark:focus:bg-neutral-800 tw:dark:focus:text-neutral-50";

interface DropdownSelectOption {
  value: string;
  label: string;
  // Shown smaller and greyed after the label, e.g. the dates a reporting year covers
  detail?: string;
  // A heading above the first option of each group, so options with the same group sit together
  group?: string;
}

interface DropdownSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: DropdownSelectOption[];
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  // Whether the closed dropdown shows the selected option's detail after its label. The open list
  // always shows each option's detail
  showTriggerDetail?: boolean;
}

function DropdownSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select an option",
  className,
  triggerClassName,
  showTriggerDetail = true,
}: DropdownSelectProps) {
  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <DropdownMenuPrimitive.Root modal={false}>
      <DropdownMenuPrimitive.Trigger
        className={cn(DROPDOWN_TRIGGER_CLASS, triggerClassName)}
        data-placeholder={!selectedOption ? "" : undefined}
      >
        <span className="tw:line-clamp-1">
          {selectedOption?.label ?? placeholder}
          {showTriggerDetail && selectedOption?.detail && (
            <OptionDetail detail={selectedOption.detail} />
          )}
        </span>
        <ChevronDownIcon className="tw:size-4 tw:opacity-50" />
      </DropdownMenuPrimitive.Trigger>

      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          className={cn(DROPDOWN_CONTENT_CLASS, className)}
          sideOffset={4}
          align="start"
        >
          {options.map((option, index) => (
            <React.Fragment key={option.value}>
              {option.group && option.group !== options[index - 1]?.group && (
                <DropdownMenuPrimitive.Label className="tw:px-2 tw:pt-2 tw:pb-1 tw:text-xs tw:font-medium tw:tracking-wider tw:text-neutral-500 tw:uppercase">
                  {option.group}
                </DropdownMenuPrimitive.Label>
              )}
              <DropdownMenuPrimitive.Item
                className={cn(DROPDOWN_ITEM_CLASS)}
                onSelect={() => onValueChange(option.value)}
              >
                <span className="tw:absolute tw:right-2 tw:flex tw:size-3.5 tw:items-center tw:justify-center">
                  {value === option.value && <CheckIcon className="tw:size-4" />}
                </span>
                <span>
                  {option.label}
                  {option.detail && <OptionDetail detail={option.detail} />}
                </span>
              </DropdownMenuPrimitive.Item>
            </React.Fragment>
          ))}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

function OptionDetail({ detail }: { detail: string }) {
  return <span className="tw:ml-2 tw:text-xs tw:text-neutral-500">{detail}</span>;
}

export { DropdownSelect };
export type { DropdownSelectOption, DropdownSelectProps };
